import { fetcher } from '@data/utils/api/fetcher/fetcher';
import formatDate from '@data/utils/format-date';
import {
  getMetricFormat,
  STUDENT_LEVEL_METRICS,
  type MetricV2Response,
  type MetricV2Type,
} from '@data/config/metrics';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';

// ── Response schema ────────────────────────────────────────────────────────────

/**
 * One entry per requested metric, tagged with the metric it answers.
 *
 * The single-metric route does not tag its response — the caller knows what it
 * asked for from the path. A batched response is a flat list, so each entry has
 * to identify itself.
 *
 * `value`, `toolTip`, `dateOfAssessment` and the counts are `nullish` rather than
 * `nullable`: the service serializes with `NON_NULL`, so a null field is absent
 * from the payload rather than present as `null`.
 */
const metricBatchV2EntrySchema = z.object({
  metricType: z.string(),
  value: z.string().nullish(),
  label: z.string(),
  toolTip: z.string().nullish(),
  dateOfAssessment: z.string().nullish(),
});

const metricsBatchV2Schema = z.array(metricBatchV2EntrySchema);

/** A batched entry, normalized to the same shape the single-metric hook returns. */
export type MetricBatchV2Response = MetricV2Response & {
  metricType: MetricV2Type;
};

// ── Request body ───────────────────────────────────────────────────────────────

export interface MetricsBatchV2Request {
  orgId: string;
  /** Duplicates are collapsed by the service; the response preserves this order. */
  metricTypes: MetricV2Type[];
  teacherIds: string[];
  startDate: Date;
  endDate: Date;
  edApps: string[];
  classIds?: string[] | null;
  studentIds?: string[] | null;
  /**
   * The single student LEXILE_MEASURE and GE_SCORE report on. Defaults to the
   * first of `studentIds`, mirroring the single-metric hook.
   */
  studentId?: string | null;
}

export interface UseMetricsBatchV2ApiProps extends MetricsBatchV2Request {
  enabled?: boolean;
}

// ── Fetcher ────────────────────────────────────────────────────────────────────

function hasStudentLevelMetric(metricTypes: MetricV2Type[]) {
  return metricTypes.some((metricType) =>
    STUDENT_LEVEL_METRICS.includes(metricType)
  );
}

/**
 * Builds the request body.
 *
 * Which fields the service requires depends on what is being asked for:
 * `teacherIds` for every roster-scoped metric, `studentId` whenever
 * LEXILE_MEASURE or GE_SCORE is present. Both can be required at once — a
 * student-level drilldown asks for roster metrics and a Lexile measure in the
 * same call — so neither branch excludes the other.
 *
 * Empty id lists are sent as `null`, not `[]`: the service rejects an empty
 * array but treats null as "no filter".
 */
export function buildMetricsBatchV2Body({
  orgId,
  metricTypes,
  teacherIds,
  startDate,
  endDate,
  edApps,
  classIds,
  studentIds,
  studentId,
}: MetricsBatchV2Request) {
  return {
    orgId,
    metricTypes,
    teacherIds: teacherIds.length > 0 ? teacherIds : null,
    startDate: formatDate(startDate),
    endDate: formatDate(endDate, { boundary: 'end' }),
    edApps,
    classIds: classIds && classIds.length > 0 ? classIds : null,
    studentIds: studentIds && studentIds.length > 0 ? studentIds : null,
    ...(hasStudentLevelMetric(metricTypes)
      ? { studentId: studentId ?? studentIds?.[0] ?? null }
      : {}),
  };
}

export const getMetricsBatchV2Api = async (
  request: MetricsBatchV2Request
): Promise<MetricBatchV2Response[]> => {
  const response = await fetcher('/metrics/batch', {
    service: '/data',
    version: '/v2',
    method: 'POST',
    body: JSON.stringify(buildMetricsBatchV2Body(request)),
  });

  return metricsBatchV2Schema.parse(response).map((entry) => {
    const metricType = entry.metricType as MetricV2Type;

    return {
      metricType,
      label: entry.label,
      value: entry.value ?? null,
      format: getMetricFormat(metricType),
      ...(entry.toolTip ? { toolTip: entry.toolTip } : {}),
      ...(entry.dateOfAssessment
        ? { dateOfAssessment: entry.dateOfAssessment }
        : {}),
    };
  });
};

// ── Query key ──────────────────────────────────────────────────────────────────

export function metricsBatchV2QueryKey(request: MetricsBatchV2Request) {
  const {
    metricTypes,
    orgId,
    teacherIds,
    startDate,
    endDate,
    edApps,
    classIds,
    studentIds,
    studentId,
  } = request;

  return [
    'metrics-batch-v2',
    {
      metricTypes,
      orgId,
      teacherIds,
      startDate: formatDate(startDate),
      endDate: formatDate(endDate, { boundary: 'end' }),
      edApps,
      classIds: classIds ?? null,
      studentIds: studentIds ?? null,
      studentId: studentId ?? studentIds?.[0] ?? null,
    },
  ] as const;
}

// ── Hook ───────────────────────────────────────────────────────────────────────

/**
 * Every metric the caller needs in one request.
 *
 * Replaces the per-metric fan-out: the service answers metrics sharing a source
 * table from a single query and runs those queries concurrently, so one call
 * costs the slowest query rather than the sum of N round trips.
 *
 * There is deliberately no single-metric use of this route — grouping does
 * strictly more work than a narrowly targeted query, so `useMetricsV2Api` stays
 * faster when only one metric is needed.
 */
export function useMetricsBatchV2Api({
  enabled,
  ...request
}: UseMetricsBatchV2ApiProps): UseQueryResult<MetricBatchV2Response[], Error> {
  return useQuery({
    queryKey: metricsBatchV2QueryKey(request),
    queryFn: () => getMetricsBatchV2Api(request),
    enabled: enabled !== false && request.metricTypes.length > 0,
    staleTime: 3 * 60 * 1000, // 3 Min Stale Time
    refetchOnWindowFocus: false,
  });
}
